import React, { useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import { useAtomValue, useSetAtom } from 'jotai'

import {
	PageTitle,
	AffiliateListTable,
	AffiliateEditModal,
	AffiliateEditNotesModal,
	UiPagination,
	ToolPanel,
	ESortOrder
} from '../components'
import { Filters, FiltersButtonIcon } from '../components/filters'
import { useAffiliateList, useAffiliateFiltersPage, useAffiliateSortPage } from '../hooks'
import * as lib from '../lib'
import * as state from '../states'
import { IFetchItemsSetup, IAffiliate, ERoleAccess } from '../types'
import { TAKE, MENU, MENU_CATEGORY } from '../constants'
import { Access } from '../lib'
import { useTranslation } from 'react-i18next'
import { ALL_LANG_KEY } from '../types/lang'

export const AffiliatePage = (): React.JSX.Element => {
	const { t } = useTranslation()
	const setFetchIsCommonToReload = useSetAtom(state.fetchIsCommonToReloadAtom)
	const tokens = useAtomValue(state.tokensAtom)
	const access = tokens?.access ?? null
	const setFetchIsLoading = useSetAtom(state.fetchIsLoadingAtom)

	const profile = useAtomValue(state.profileAtom)
	const $access = new Access(profile ?? null)

	const location = useLocation()
	const href = lib.pageHref(location, TAKE)
	const navigate = useNavigate()
	const [searchParams, setSearchParams] = useSearchParams()

	const page = lib.skipSearchParams(searchParams.get('page')) ?? 0
	const take = lib.takeSearchParams(searchParams.get('take')) ?? TAKE
	const skip = (take === 'all') ? 0 : page > 0 ? page * take : 0

	const pageTitle = `${t(MENU_CATEGORY.revenue.title)} ${t(MENU.affiliate.title)}`

	const {
		filter,
		send,
		activeFiltersCount,
		filterFillFactor,
		isFiltersPanelOpen,
		setIsFiltersPanelOpen,
		affiliateFiltersConfig,
		defaultFiltersValue,
		initialFiltersValue,
		clearChildFilters,
		setUserFilters
	} = useAffiliateFiltersPage()

	//TODO: check sort output var type ?
	const {
		// sort: sortTmp,
		sortBy,
		setSortBy,
		order,
		setOrder
	} = useAffiliateSortPage()

	const setup: IFetchItemsSetup = {
		filter,
		sort: order !== ESortOrder.unset ? [[sortBy, order]] : [],
		skip,
		take
	}
	const { error, items, total, warmUp } = useAffiliateList(setup, send)

	const [editAffiliate, setEditAffiliate] = useState<IAffiliate | null>(null)
	const [editNotesAffiliate, setEditNotesAffiliate] = useState<IAffiliate | null>(null)

	const onEdit = (item: IAffiliate, e: React.MouseEvent) => {
		e.preventDefault()
		setEditAffiliate(item)
	}
	const onEditNotes = (item: IAffiliate, e: React.MouseEvent) => {
		e.preventDefault()
		setEditNotesAffiliate(item)
	}

	return (
		<>
			<PageTitle title={pageTitle}/>

			<div className="card rounded-3 border-0 mb-24">
				<div className="card-body p-25">
					<div className="d-flex align-items-center justify-content-between pb-20 border-bottom border-color">
						<FiltersButtonIcon
							activeFiltersCount={activeFiltersCount}
							filterFillFactor={filterFillFactor}
							onClick={() => setIsFiltersPanelOpen((val) => !val)}
						/>
					</div>

					<Filters
						config={affiliateFiltersConfig}
						initialFiltersValue={initialFiltersValue}
						resetFiltersValue={defaultFiltersValue}
						isAutoModeWithDebounce={false}
						isFiltersPanelOpen={isFiltersPanelOpen}
						isFiltersToClear={clearChildFilters}
						setFiltersCB={(value) => {
							//console.log('page.AffiliatePage::setFiltersCB::', { value })
							setUserFilters(value)
						}}
					/>

					<AffiliateListTable
						items={items}
						skip={skip}
						sortBy={sortBy}
						setSortBy={setSortBy}
						order={order}
						setOrder={setOrder}
						onEdit={onEdit}
						onEditNotes={onEditNotes}
					/>

					<ToolPanel flow="<-">
						<UiPagination
							classesToAdd={{
								wrapper: 'mt-25'
							}}
							page={page}
							take={take}
							total={total}
							href={(page: number) => href(page).tail}
						/>
					</ToolPanel>
				</div>
			</div>

			{
				!!editAffiliate && !!$access.$(ERoleAccess.AffiliateModify) && <AffiliateEditModal
					item={editAffiliate}
					show={!!editAffiliate}
					onClose={() => setEditAffiliate(null)}
					onSave={() => {
						setEditAffiliate(null)
						navigate(href(0).tail)
						warmUp()
					}}
				/>
			}

			{
				!!editNotesAffiliate && !!$access.$(ERoleAccess.AffiliateList) && <AffiliateEditNotesModal
					isViewMode={true}
					item={editNotesAffiliate}
					show={!!editNotesAffiliate}
					onClose={() => setEditNotesAffiliate(null)}
					onSave={() => {
						setEditNotesAffiliate(null)
						//navigate(href(0).tail)
						warmUp()
					}}
				/>
			}
		</>
	)
}
